// src/components/DeptMetricTable.tsx (v2、 2026-05-26)
// サイドバー「注目指標」(f.metric) の 科別 × 月 集計表。
// セル色 = 対前年同月 OR 対予算 (±5%/±10% の 5 階調、 direction を尊重) — トグル切替。
// 右側固定列: 月平均 / 月平均(前年) / 月平均(予算) / 対前年 / 対予算 (showBudget=true 時のみ予算系)。
// 月平均 降順でソート。
//
// showBudget=true (予算・前年度比較タブ用) で 月平均(予算) + 対予算 列 + 色トグル を有効化。
// showBudget=false (後方互換、 旧 入院実績タブ) では 対前年 のみ。
import { useMemo, useState } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters } from "../store";
import { CsvButton } from "./CsvButton";
import { useSort, SortTh } from "../lib/useSort";
import { fmtByUnitValueOnly } from "../lib/format";
import { RATIO_METRICS, weightedRatio, isRatioMetric } from "../lib/ratioMetrics";
import type { Direction, MetricRow, MonthlyResponse } from "@shared/api";
import { gpText } from "../lib/gpMode";

type ColorMode = "yoy" | "budget";

interface Props { metrics: MetricRow[]; showBudget?: boolean; }

const cellBg = (pct: number | null, direction: Direction | undefined): string => {
  if (pct == null || direction === "neutral") return "";
  const p = direction === "lower_is_better" ? -pct : pct;
  if (p >= 0.10) return "bg-emerald-300";
  if (p >= 0.05) return "bg-emerald-100";
  if (p >= -0.05) return "";
  if (p >= -0.10) return "bg-red-100";
  return "bg-red-300";
};

export function DeptMetricTable({ metrics, showBudget = false }: Props) {
  const f = useFilters();
  const avgMode = f.periodPreset === "直近12ヶ月平均";
  const [colorMode, setColorMode] = useState<ColorMode>("yoy");
  const metricDef = metrics.find((x) => x["集計区分"] === f.metric);
  const unit = metricDef?.unit ?? "";
  const direction = metricDef?.direction;
  const METRIC_LABEL_MAP: Record<string, string> = {
    "入院単価": "1日入院単価",
    "在院単価": "1日在院単価",
    "GP": "粗利",
    "GP単価": "1日粗利単価",
    // [2026-08-24] 生名「GP単価_在院」が正式版の指標一覧にそのまま出ていたので追加
    "GP単価_在院": "1日粗利単価(在院)",
    "DPC期間2以内率": "DPCⅡ以内率",
    "常勤換算数": "常勤医師数(入院診療科)",
  };
  const displayMetric = gpText(METRIC_LABEL_MAP[f.metric] ?? f.metric);

  const { data, isLoading } = useQuery<MonthlyResponse>({
    queryKey: ["dept_metric_table", f.granularity, f.iryo, f.yotei, f.metric, f.periodFrom, f.periodTo],
    queryFn: () => {
      // 前年同月比のため periodFrom を 12 ヶ月前まで広げて取得
      const [y, mo, d] = f.periodFrom.split("-").map(Number);
      const pyStr = new Date(Date.UTC(y - 1, mo - 1, d)).toISOString().slice(0, 10);
      return api.monthly({
        granularity: f.granularity, iryo: f.iryo, yotei: f.yotei,
        metric: f.metric, dateFrom: pyStr, dateTo: f.periodTo,
      });
    },
    placeholderData: (prev) => prev,
  });

  // 比指標(単価・率・平均在院日数 等)は期間集計を Σ分子/Σ分母 で加重する (2026-06-30)。
  //   分子/分母の構成指標を追加 fetch し、直近12ヶ月平均モードの集計列だけ加重に差し替える。
  const ratioSpec = isRatioMetric(f.metric) ? RATIO_METRICS[f.metric] : null;
  const weightable = !!ratioSpec && !ratioSpec.needsData && !ratioSpec.unitDays && ratioSpec.den.length > 0;
  const compMetrics = weightable ? [...new Set([...ratioSpec!.num, ...ratioSpec!.den])] : [];
  const compQueries = useQueries({
    queries: compMetrics.map((m) => ({
      queryKey: ["dmt_comp", f.granularity, f.iryo, f.yotei, m, f.periodFrom, f.periodTo],
      queryFn: () => {
        const [y, mo, d] = f.periodFrom.split("-").map(Number);
        const py = new Date(Date.UTC(y - 1, mo - 1, d)).toISOString().slice(0, 10);
        return api.monthly({ granularity: f.granularity, iryo: f.iryo, yotei: f.yotei, metric: m, dateFrom: py, dateTo: f.periodTo });
      },
      placeholderData: (prev: MonthlyResponse | undefined) => prev,
    })),
  });

  type DeptCells = {
    actual: Map<string, number>;       // 期間内: month → 実績
    prev:   Map<string, number>;       // 当月キー → 前年同月実績 (date+12M で index)
    budget: Map<string, number>;       // 期間内: month → 予算
  };
  type Row = {
    診療科:      string;
    cells:       DeptCells;
    actualMean:  number | null;
    prevMean:    number | null;
    budgetMean:  number | null;
    nA:          number;            // actual がある月数 (coverage 指標表示用)
    nPY:         number;
    nBud:        number;
    yoyDelta:    number | null;   // 月平均ベースの (actual-prev)/|prev| (色判定用、 coverage 一致時のみ)
    yoyRatio:    number | null;   // 月平均ベースの actual/prev (表示用、 coverage 一致時のみ)
    budDelta:    number | null;
    budRatio:    number | null;
    grade:       "A" | "B" | "C" | null;   // 達成度 (予算達成A/未達でも前年超B/それ以外C)
  };

  // 「病院全体」行に使う 総合計系 dept 名
  const totalDeptName = (f.granularity === "小" || f.iryo === "合計") ? "総合計"
                      : f.iryo === "急性期" ? "急性期合計"
                      : "回復期合計";

  // 比加重用: 構成指標を 診療科×月 で索引化 (本体 byDept と同じ規則)。
  const compByDept = useMemo(() => {
    const out = new Map<string, { byDept: Map<string, DeptCells>; total: DeptCells | null }>();
    const totalNames = new Set(["総合計", "急性期合計", "回復期合計"]);
    const empty = (): DeptCells => ({ actual: new Map(), prev: new Map(), budget: new Map() });
    compMetrics.forEach((m, i) => {
      const cd = compQueries[i]?.data;
      if (!cd) return;
      const byDept = new Map<string, DeptCells>();
      let total: DeptCells | null = null;
      for (const r of cd.rows) {
        const isTotal = r["診療科"] === totalDeptName;
        if (totalNames.has(r["診療科"]) && !isTotal) continue;
        const target = isTotal ? (total ?? (total = empty()))
          : (byDept.get(r["診療科"]) ?? (() => { const x = empty(); byDept.set(r["診療科"], x); return x; })());
        if (r.date >= f.periodFrom && r.date <= f.periodTo) {
          // 2026-07-17: 実績 は null がありうる (分母0 の比指標 / 未来月)。
          //   Number(null) === 0 なので、無防備に set すると NULL が 0 セルに化け、
          //   下の `v != null ? ... : "—"` の欠測分岐が到達不能になっていた。
          //   予算側 (次行) は元から != null ガードがあり、非対称だった。
          if (r.実績 != null) target.actual.set(r.date, Number(r.実績));
          if (r.予算 != null) target.budget.set(r.date, Number(r.予算));
        }
        const [y, mo, d] = r.date.split("-").map(Number);
        const next = new Date(Date.UTC(y + 1, mo - 1, d)).toISOString().slice(0, 10);
        if (r.実績 != null) target.prev.set(next, Number(r.実績));
      }
      out.set(m, { byDept, total });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compMetrics.join(","), compQueries.map((q) => q.dataUpdatedAt).join(","), f.periodFrom, f.periodTo, totalDeptName]);

  const { tableRows, months } = useMemo(() => {
    if (!data) return { tableRows: [] as Row[], months: [] as string[] };
    const totalNames = new Set(["総合計", "急性期合計", "回復期合計"]);
    const ms = new Set<string>();
    const byDept = new Map<string, DeptCells>();
    let totalCells: DeptCells | null = null;
    const empty = (): DeptCells => ({ actual: new Map(), prev: new Map(), budget: new Map() });
    for (const r of data.rows) {
      const isTotalDept = r["診療科"] === totalDeptName;
      const isOtherTotal = totalNames.has(r["診療科"]) && !isTotalDept;
      if (isOtherTotal) continue;
      const target = isTotalDept
        ? (totalCells ?? (totalCells = empty()))
        : (byDept.get(r["診療科"]) ?? (() => { const x = empty(); byDept.set(r["診療科"], x); return x; })());
      if (r.date >= f.periodFrom && r.date <= f.periodTo) {
        ms.add(r.date);
        // 2026-07-17: 実績 の null ガード (上の compByDept と同じ理由)。
        //   Map にキーが無ければ get() が undefined を返し、セルは "—" になる。
        if (r.実績 != null) target.actual.set(r.date, Number(r.実績));
        if (r.予算 != null) target.budget.set(r.date, Number(r.予算));
      }
      // 前年同月: date+12M を key として登録 (= 当月から見た前年値)
      const [y, mo, d] = r.date.split("-").map(Number);
      const next = new Date(Date.UTC(y + 1, mo - 1, d)).toISOString().slice(0, 10);
      if (r.実績 != null) target.prev.set(next, Number(r.実績));
    }
    const months = [...ms].sort();

    // 直近12ヶ月平均モード: 末尾12ヶ月 (= 表示期間の最後の12ヶ月) の単純平均で集計。
    const last12 = months.slice(-12);
    const meanOf = (m: Map<string, number>): number | null => {
      const vals = last12.map((mo) => m.get(mo)).filter((v): v is number => v != null);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    // 比指標: 構成指標を直近12ヶ月で Σ し、Σ分子/Σ分母 で加重(月別比の単純平均でなく)。
    const sumComp = (metric: string, dep: string, field: "actual" | "prev" | "budget"): number | null => {
      const cc = compByDept.get(metric);
      if (!cc) return null;
      const cells = dep === "病院全体" ? cc.total : cc.byDept.get(dep);
      if (!cells) return null;
      let s = 0, n = 0;
      for (const mo of last12) { const v = cells[field].get(mo); if (v != null) { s += v; n++; } }
      return n > 0 ? s : null;
    };
    const wRatio = (dep: string, field: "actual" | "prev" | "budget"): number | null =>
      weightable ? weightedRatio(ratioSpec!, (m) => sumComp(m, dep, field)) : null;
    const toRow = (name: string, c: DeptCells): Row => {
      // 右側集計: 単月モード = 選択月(期間の最終月)基準 / 直近12ヶ月平均モード = 末尾12ヶ月平均。
      //   並び替え用に actualMean (期間平均) も残すが表示はしない。
      let sa = 0, ca = 0;
      for (const mo of months) {
        const a = c.actual.get(mo);
        if (a != null) { sa += a; ca++; }
      }
      const actualMean = ca > 0 ? sa / ca : null;
      const selMo = months[months.length - 1];
      const selA       = avgMode ? (weightable ? wRatio(name, "actual") : meanOf(c.actual)) : (selMo ? (c.actual.get(selMo) ?? null) : null);
      const prevMean   = avgMode ? (weightable ? wRatio(name, "prev")   : meanOf(c.prev))   : (selMo ? (c.prev.get(selMo)   ?? null) : null);  // 前年(同月/平均)
      const budgetMean = avgMode ? (weightable ? wRatio(name, "budget") : meanOf(c.budget)) : (selMo ? (c.budget.get(selMo) ?? null) : null);  // 予算(選択月/平均)
      const yoyRatio = (selA != null && prevMean   != null && prevMean   !== 0) ? selA / prevMean   : null;
      const budRatio = (selA != null && budgetMean != null && budgetMean !== 0) ? selA / budgetMean : null;
      const yoyDelta = yoyRatio != null ? yoyRatio - 1 : null;
      const budDelta = budRatio != null ? budRatio - 1 : null;
      const grade = gradeOf(selA, budgetMean, prevMean, direction);
      return { 診療科: name, cells: c, actualMean, prevMean, budgetMean, nA: ca, nPY: ca, nBud: ca, yoyDelta, yoyRatio, budDelta, budRatio, grade };
    };

    const deptRows = [...byDept].map(([dep, c]) => toRow(dep, c));
    // 2026-06-16: 色分けトグルに連動 — 前年比色分け=対前年 降順 / 予算比色分け=対予算 降順
    const sortByBudget = showBudget && colorMode === "budget";
    deptRows.sort((a, b) => {
      const av = sortByBudget ? a.budRatio : a.yoyRatio;
      const bv = sortByBudget ? b.budRatio : b.yoyRatio;
      return (bv ?? -Infinity) - (av ?? -Infinity);
    });

    const out: Row[] = [];
    if (totalCells) out.push(toRow("病院全体", totalCells));
    out.push(...deptRows);
    return { tableRows: out, months };
  }, [data, f.periodFrom, f.periodTo, totalDeptName, direction, avgMode, colorMode, showBudget, compByDept, weightable, ratioSpec]);

  // 表示は末尾12ヶ月 (集計/選択月は内部的に全期間を使うが、 セル表示は12ヶ月)
  const displayMonths = months.length > 12 ? months.slice(-12) : months;

  // クリックソート。「病院全体」は pin で先頭固定。未クリック時は tableRows 原順(対前年/対予算 降順の固定sort)。
  const s = useSort(tableRows, { pin: (r) => r.診療科 === "病院全体" });

  const monthLabel = (s: string) => {
    const [y, mo] = s.split("-");
    const mi = parseInt(mo, 10);
    return mi === 1 || mi === 4 ? `${y.slice(2)}/${mi}月` : `${mi}月`;
  };

  const csvRows = useMemo(() => tableRows.map((r) => {
    const row: Record<string, string | number> = {
      診療科: r.診療科,
      前年同月: r.prevMean ?? "",
      "対前年%": r.yoyRatio != null ? Number((r.yoyRatio * 100).toFixed(1)) : "",
    };
    if (showBudget) {
      row["選択月予算"] = r.budgetMean ?? "";
      row["対予算%"] = r.budRatio != null ? Number((r.budRatio * 100).toFixed(1)) : "";
    }
    months.forEach((mo) => { row[mo] = r.cells.actual.get(mo) ?? ""; });
    return row;
  }), [tableRows, months, showBudget]);

  // ヘッダ右側集計列 (2026-06-16: 前年同月 / 選択月予算 / 対前年 / 対予算 / 達成度。 達成度は一番右)
  const renderRightColsHead = () => (
    <>
      <SortTh s={s} sortKey="前年" get={(r) => (r as Row).prevMean} className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">{avgMode ? "前年平均" : "前年同月"}</SortTh>
      {showBudget && <SortTh s={s} sortKey="予算" get={(r) => (r as Row).budgetMean} className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">{avgMode ? "予算平均" : "選択月予算"}</SortTh>}
      <SortTh s={s} sortKey="対前年" get={(r) => (r as Row).yoyRatio} className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">対前年</SortTh>
      {showBudget && <SortTh s={s} sortKey="対予算" get={(r) => (r as Row).budRatio} className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">対予算</SortTh>}
      {showBudget && <SortTh s={s} sortKey="達成度" get={(r) => (r as Row).grade} className="text-center px-1.5 py-1 border-b border-slate-200 bg-slate-200">達成度</SortTh>}
    </>
  );

  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-base font-semibold">
          📌 注目指標の科別実績:{displayMetric}{unit ? `(${unit})` : ""}
        </h2>
        <span className="text-xs text-slate-500">サイドバー「📌 注目指標」で切替</span>
        {isLoading && <span className="text-xs text-slate-400">loading…</span>}
        {/* トグル + CSV をまとめて右寄せ (AllMetricsTable と同じ位置に揃える) */}
        <div className="ml-auto flex items-center gap-2">
          {showBudget && (
            <div className="inline-flex rounded border border-slate-300 overflow-hidden text-xs">
              <button type="button"
                      onClick={() => setColorMode("yoy")}
                      className={`px-3 py-1 ${colorMode === "yoy"
                        ? "bg-amber-500 text-white font-semibold"
                        : "bg-white text-slate-600 hover:bg-slate-50"}`}
                      title="セル色 = 当月÷前年同月 を 5 階調で表示">前年比で色分け</button>
              <button type="button"
                      onClick={() => setColorMode("budget")}
                      className={`px-3 py-1 border-l border-slate-300 ${colorMode === "budget"
                        ? "bg-amber-500 text-white font-semibold"
                        : "bg-white text-slate-600 hover:bg-slate-50"}`}
                      title="セル色 = 当月÷予算 を 5 階調で表示">予算比で色分け</button>
            </div>
          )}
          <CsvButton
            rows={csvRows as unknown as Record<string, unknown>[]}
            filename={`dept_metric_${f.metric}_${f.granularity}_${f.periodFrom.slice(0,7)}-${f.periodTo.slice(0,7)}`}
          />
        </div>
      </div>
      <div className="text-[10px] text-slate-500 mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">セル色 ({showBudget && colorMode === "budget" ? "対予算" : "対前年同月"}):</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-300 border border-slate-300"></span>+10% 以上</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-100 border border-slate-300"></span>+5% 以上</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-white border border-slate-300"></span>±5% 内</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-100 border border-slate-300"></span>-5% 以下</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-300 border border-slate-300"></span>-10% 以下</span>
        <span>・「減るほど良い」指標 (平均在院日数 等) は色が反転</span>
      </div>

      {tableRows.length === 0 ? (
        <div className="text-xs text-slate-400 py-8 text-center">データなし (フィルタを確認)</div>
      ) : (
        <div className="overflow-x-auto text-xs">
          <table className="w-full border-collapse">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr>
                <SortTh s={s} sortKey="診療科" get={(r) => (r as Row).診療科} className="text-left px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-20">診療科</SortTh>
                {displayMonths.map((mo) => (
                  <SortTh key={mo} s={s} sortKey={`month:${mo}`} get={(r) => (r as Row).cells.actual.get(mo) ?? null} className="text-right px-1 py-1 border-b border-slate-200 tabular-nums">{monthLabel(mo)}</SortTh>
                ))}
                {renderRightColsHead()}
                <SortTh s={s} sortKey="診療科R" get={(r) => (r as Row).診療科} className="text-left px-2 py-1 border-b border-slate-200 bg-slate-100">診療科</SortTh>
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r, ri) => {
                // 月平均ベースの 対前年/対予算 (右側 ratio 列の色判定にも使用)
                const yoyBgRight = cellBg(r.yoyDelta, direction);
                const budBgRight = cellBg(r.budDelta, direction);
                const isTotal = r.診療科 === "病院全体";
                const isFocused = isTotal ? f.focusedDept === null : f.focusedDept === r.診療科;
                const onClick = () => {
                  if (isTotal) f.setFocusedDept(null);
                  else f.setFocusedDept(isFocused ? null : r.診療科);
                };
                const focusFrame = isFocused;
                const stripe = !focusFrame && ri % 2 === 1;  // ゼブラ (薄グレー/白 交互)
                return (
                  <tr key={r.診療科}
                      className={focusFrame
                        ? "border-y-2 border-amber-500"
                        : `border-b border-slate-100 hover:bg-blue-50/40 ${stripe ? "bg-slate-50" : ""}`}>
                    <td className={`text-left px-1.5 py-0.5 sticky left-0 z-[1] font-medium ${focusFrame ? "bg-white border-l-2 border-amber-500" : stripe ? "bg-slate-50" : "bg-white"}`}>
                      <button
                        onClick={onClick}
                        className={`text-left ${focusFrame ? "font-bold" : "hover:underline decoration-dotted"}`}
                        title={
                          isTotal ? "病院全体 (= 全診療科 合計)。 クリックでフォーカス解除"
                          : isFocused ? "クリックでフォーカス解除"
                          : "この診療科にフォーカス"
                        }
                      >
                        {isTotal ? <span className="mr-1">🏥</span> : isFocused ? <span className="mr-1">🎯</span> : null}
                        {r.診療科}
                      </button>
                    </td>
                    {displayMonths.map((mo) => {
                      const v = r.cells.actual.get(mo);
                      const p = r.cells.prev.get(mo);
                      const b = r.cells.budget.get(mo);
                      // セル色: colorMode に応じて 前年 OR 予算 を ref に
                      const ref = (showBudget && colorMode === "budget") ? b : p;
                      const pct = (v != null && ref != null && ref !== 0) ? (v - ref) / Math.abs(ref) : null;
                      const bg = cellBg(pct, direction);
                      const refLabel = (showBudget && colorMode === "budget") ? "予算" : "前年";
                      const ratioPct = (v != null && ref != null && ref !== 0) ? `${(v / ref * 100).toFixed(1)}%` : "—";
                      const tooltip = v != null
                        ? `${monthLabel(mo)}\n実績: ${fmtByUnitValueOnly(v, unit)}` +
                          (p != null ? `\n前年: ${fmtByUnitValueOnly(p, unit)}` : "") +
                          (b != null ? `\n予算: ${fmtByUnitValueOnly(b, unit)}` : "") +
                          (ref != null ? `\n対${refLabel}: ${ratioPct}` : "")
                        : "データなし";
                      return (
                        <td key={mo} className={`text-right px-1 py-0.5 tabular-nums ${bg}`} title={tooltip}>
                          {v != null ? fmtByUnitValueOnly(v, unit) : "—"}
                        </td>
                      );
                    })}
                    <td className="text-right px-1.5 py-0.5 tabular-nums bg-slate-50 text-slate-600"
                        title={`${avgMode ? "前年平均" : "前年同月"}: ${r.prevMean != null ? fmtByUnitValueOnly(r.prevMean, unit) : "—"}`}>
                      {r.prevMean != null ? fmtByUnitValueOnly(r.prevMean, unit) : "—"}
                    </td>
                    {showBudget && (
                      <td className="text-right px-1.5 py-0.5 tabular-nums bg-slate-50 text-slate-600"
                          title={`${avgMode ? "予算平均" : "選択月予算"}: ${r.budgetMean != null ? fmtByUnitValueOnly(r.budgetMean, unit) : "—"}`}>
                        {r.budgetMean != null ? fmtByUnitValueOnly(r.budgetMean, unit) : "—"}
                      </td>
                    )}
                    <td className={`text-right px-1.5 py-0.5 tabular-nums font-semibold ${yoyBgRight} ${focusFrame && !showBudget ? "border-r-2 border-amber-500" : ""}`}
                        title={`対前年 = ${avgMode ? "直近12ヶ月平均" : "選択月実績"} ÷ ${avgMode ? "前年平均" : "前年同月"} = ${r.yoyRatio != null ? `${(r.yoyRatio * 100).toFixed(1)}%` : "—"}`}>
                      {r.yoyRatio != null ? `${(r.yoyRatio * 100).toFixed(1)}%` : "—"}
                    </td>
                    {showBudget && (
                      <td className={`text-right px-1.5 py-0.5 tabular-nums font-semibold ${budBgRight}`}
                          title={`対予算 = ${avgMode ? "直近12ヶ月平均" : "選択月実績"} ÷ ${avgMode ? "予算平均" : "選択月予算"} = ${r.budRatio != null ? `${(r.budRatio * 100).toFixed(1)}%` : "—"}`}>
                        {r.budRatio != null ? `${(r.budRatio * 100).toFixed(1)}%` : "—"}
                      </td>
                    )}
                    {showBudget && (
                      <td className={`text-center px-1.5 py-0.5 bg-slate-50 ${focusFrame ? "border-r-2 border-amber-500" : ""}`}
                          title="達成度: 予算達成=A / 予算未達でも前年超=B / それ以外=C">
                        {r.grade
                          ? <span className={`inline-flex w-5 h-5 rounded-full items-center justify-center text-[11px] font-extrabold ${gradeStyle(r.grade)}`}>{r.grade}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                    )}
                    {/* 一番右にも 診療科 (横移動の負荷軽減) */}
                    <td className={`text-left px-2 py-0.5 whitespace-nowrap ${focusFrame ? "font-bold text-slate-800 border-r-2 border-amber-500" : "text-slate-600"}`}>
                      {isTotal ? "🏥 " : isFocused ? "🎯 " : ""}{r.診療科}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// 達成度 A/B/C (2026-06-15): 予算達成=A / 予算未達でも前年超=B / それ以外=C
// (lower_is_better は下回りが達成)。 評価できる基準が無ければ null。
function gradeOf(cur: number | null, budget: number | null, prevYear: number | null, direction: Direction | undefined): "A" | "B" | "C" | null {
  if (cur == null || direction == null || direction === "neutral") return null;
  const beats = (ref: number | null): boolean | null =>
    ref == null ? null : (direction === "lower_is_better" ? cur <= ref : cur >= ref);
  const budOk = beats(budget), pyOk = beats(prevYear);
  if (budOk === true) return "A";
  if (pyOk === true) return "B";
  if (budOk == null && pyOk == null) return null;
  return "C";
}
function gradeStyle(g: "A" | "B" | "C"): string {
  return g === "A" ? "bg-blue-600 text-white" : g === "B" ? "bg-amber-400 text-white" : "bg-red-500 text-white";
}
